/**
 * ACCOUNTS.md §4 — 「找回邮箱」设置页 (`/account/email`).
 *
 * 邮箱**完全可选**：绑定了，忘记密码时就能自己收到一次性重置链接；没绑定，
 * 还是找课程老师要。流程是两步：填邮箱 → 收到 6 位验证码 → 填码。
 *
 * 这套部署没有配 SMTP 时，页面直接说明并不提供表单（服务端也会 409）。
 */
import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Field, Icon, Input, Skeleton, Tag, useConfirm, useToast } from "@xinghai/ui";
import { accountEmailApi } from "../../lib/api";
import { friendlyMessage } from "../../lib/apiErrors";
import { qk } from "../../lib/queries";

function when(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("zh-CN", { hour12: false });
}

export function EmailPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");

  const q = useQuery({
    queryKey: qk.accountEmail,
    queryFn: ({ signal }) => accountEmailApi.get(signal),
  });

  const start = useMutation({
    mutationFn: (value: string) => accountEmailApi.start(value),
    onSuccess: (data) => {
      qc.setQueryData(qk.accountEmail, data);
      setCode("");
      toast.success("验证码已经发出去了，去邮箱里找一下（可能在垃圾邮件箱）。");
    },
    onError: (err) => toast.error(friendlyMessage(err, "验证码没能发出去，请稍后再试。")),
  });

  const verify = useMutation({
    mutationFn: (value: string) => accountEmailApi.verify(value),
    onSuccess: (data) => {
      qc.setQueryData(qk.accountEmail, data);
      setEmail("");
      setCode("");
      toast.success("邮箱已绑定。以后忘记密码可以自己找回了。");
    },
    onError: (err) => toast.error(friendlyMessage(err, "验证码不对，或者已经过期了。")),
  });

  const remove = useMutation({
    mutationFn: () => accountEmailApi.remove(),
    onSuccess: (data) => {
      qc.setQueryData(qk.accountEmail, data);
      toast.success("已经解绑。忘记密码时请找课程老师要重置链接。");
    },
    onError: (err) => toast.error(friendlyMessage(err, "解绑没有成功，请重试。")),
  });

  if (q.isPending) {
    return (
      <div className="xz-container xz-stack" aria-busy="true">
        <Skeleton variant="rect" height={120} />
      </div>
    );
  }

  const state = q.data;

  return (
    <div className="xz-container xz-container--readable xz-stack">
      <h1 className="gz-type-heading">找回邮箱</h1>
      <p className="gz-muted">
        绑一个你常用的邮箱（可选）。绑好以后，忘记密码时可以自己收到一次性重置链接，
        不用再找老师。我们只用它发这一类邮件。
      </p>

      {state && !state.smtp ? (
        <Card padding="md">
          <p className="gz-notice" role="status">
            <Icon id="ui-info" size={20} />
            这套部署没有开通邮件服务，暂时不能绑定邮箱。忘记密码时请找课程老师或管理员要一个一次性重置链接。
          </p>
        </Card>
      ) : (
        <>
          <Card title="当前邮箱" padding="md">
            {state?.email ? (
              <div className="xz-stack">
                <p>
                  <Tag tone="success">
                    <Icon id="ui-check-circle" size={16} /> 已验证
                  </Tag>{" "}
                  <strong data-testid="account-email">{state.email}</strong>
                </p>
                <p className="gz-caption">验证时间：{when(state.verified_at)}</p>
                <Button
                  variant="danger"
                  loading={remove.isPending}
                  onClick={async () => {
                    const ok = await confirm({
                      title: "解绑这个邮箱？",
                      message: "解绑后就不能再自助找回密码了，要找课程老师要重置链接。",
                      confirmLabel: "解绑",
                      danger: true,
                    });
                    if (ok) remove.mutate();
                  }}
                >
                  <Icon id="ui-trash" size={20} /> 解绑
                </Button>
              </div>
            ) : (
              <p className="gz-muted">还没有绑定邮箱。</p>
            )}
          </Card>

          {state?.pending && (
            <Card title="填写验证码" padding="md">
              <form
                className="xz-stack"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (/^\d{6}$/.test(code.trim())) verify.mutate(code.trim());
                }}
              >
                <p className="gz-caption">
                  验证码已发到 <strong>{state.pending.email}</strong>，15 分钟内有效。
                </p>
                <Field label="6 位验证码">
                  <Input
                    name="code"
                    value={code}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  />
                </Field>
                <Button
                  type="submit"
                  variant="primary"
                  block
                  loading={verify.isPending}
                  disabled={!/^\d{6}$/.test(code.trim())}
                >
                  验证并绑定
                </Button>
              </form>
            </Card>
          )}

          <Card title={state?.email ? "换一个邮箱" : "绑定邮箱"} padding="md">
            <form
              className="xz-stack"
              onSubmit={(e) => {
                e.preventDefault();
                if (email.trim()) start.mutate(email.trim());
              }}
            >
              <Field label="邮箱地址">
                <Input
                  name="email"
                  type="email"
                  value={email}
                  autoComplete="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
              <Button
                type="submit"
                variant={state?.pending ? "secondary" : "primary"}
                block
                loading={start.isPending}
                disabled={email.trim().length === 0}
              >
                发送验证码
              </Button>
            </form>
          </Card>
        </>
      )}

      <p className="gz-caption">
        <Link to="/account/phone">找回手机</Link> · <Link to="/account/password">修改密码</Link> ·{" "}
        <Link to="/account/devices">我的设备</Link>
      </p>
    </div>
  );
}
